import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { QBTestQuestionEto, QbTestCoursesGroupEto } from '../../../lib/api/types';
import TestCompleteConfirmModal from '../../TestCompleteConfirmModal';
import { OpticalAnswer } from '../../../types';

interface MobileOpticalFormProps {
  isOpen: boolean;
  onClose: () => void;
  questions: QBTestQuestionEto[];
  qbGroups: QbTestCoursesGroupEto[];
  currentIndex: number;
  opticalAnswers: Record<string, OpticalAnswer>;
  onAnswerSelect: (answer: 'A' | 'B' | 'C' | 'D' | 'E' | '', questionId?: string) => void;
  onQuestionChange: (index: number) => void;
  showAnswerFeedback?: boolean;
  isTestMode?: boolean;
  totalTime?: number;
  isTestCompleted?: boolean;
  onTestComplete?: () => void;
}

export const MobileOpticalForm: React.FC<MobileOpticalFormProps> = ({
  isOpen,
  onClose,
  questions,
  qbGroups,
  currentIndex,
  opticalAnswers,
  onAnswerSelect,
  onQuestionChange,
  showAnswerFeedback = false,
  isTestMode,
  totalTime,
  isTestCompleted,
  onTestComplete
}) => {
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isUserSelection, setIsUserSelection] = useState(false);

  if (!isOpen) return null;
  
  // Debug optical answers - track re-renders
  React.useEffect(() => {
    console.log('🔍 [MobileOpticalForm] opticalAnswers updated:', {
      count: Object.keys(opticalAnswers).length,
      sample: Object.entries(opticalAnswers).slice(0, 3).map(([k, v]) => `${k.substring(0, 8)}...: ${v}`),
      allKeys: Object.keys(opticalAnswers).map(k => k.substring(0, 8) + '...')
    });
  }, [opticalAnswers]);

  // Add iOS-specific styles
  React.useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      document.body.classList.add('ios-device');
    }
    return () => {
      document.body.classList.remove('ios-device');
    };
  }, []);

  // Update selected group when current question changes (only if not user selection)
  React.useEffect(() => {
    if (!isUserSelection && qbGroups.length > 0 && currentIndex >= 0 && questions[currentIndex]) {
      const currentQuestion = questions[currentIndex];
      
      // Find which group contains the current question
      let foundGroupIndex = -1;
      
      for (let groupIndex = 0; groupIndex < qbGroups.length; groupIndex++) {
        const group = qbGroups[groupIndex];
        const hasQuestion = group.courses?.some(course => 
          course.questions?.some(q => q.questionId === currentQuestion.questionId)
        );
        
        if (hasQuestion) {
          foundGroupIndex = groupIndex;
          break;
        }
      }
      
      if (foundGroupIndex >= 0 && foundGroupIndex !== selectedGroupIndex) {
        setSelectedGroupIndex(foundGroupIndex);
      }
    }
  }, [currentIndex, qbGroups, questions]); // Removed selectedGroupIndex from dependencies
  
  // Reset user selection flag after a short delay
  React.useEffect(() => {
    if (isUserSelection) {
      const timer = setTimeout(() => {
        setIsUserSelection(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUserSelection]);

  const renderQuestion = (question: any) => {
    const globalIndex = question.globalIndex !== undefined
      ? question.globalIndex
      : questions.findIndex(q => q.questionId === question.questionId);
    const rawAnswer = opticalAnswers[question.questionId || ''];
    // Filter out '0' for display (means visited but unanswered)
    const answer = rawAnswer === '0' ? undefined : rawAnswer;
    const isCurrentQuestion = globalIndex === currentIndex;
    
    return (
      <div
        key={question.questionId}
        onClick={() => {
          if (onQuestionChange && globalIndex !== -1) {
            onQuestionChange(globalIndex);
            onClose();
          }
        }}
        className={`relative p-1.5 rounded-lg transition-all cursor-pointer sm:p-2 ${
          isCurrentQuestion 
            ? 'bg-orange-50 border-2 border-orange-400 shadow-sm' 
            : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
        }`}
      >
        {/* Answer feedback indicator - positioned on left border */}
        {answer && showAnswerFeedback && question.answer && (
          <div className="absolute -left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-0.5 shadow-sm">
            {answer === question.answer ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8l3 3 7-7" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4l8 8M12 4l-8 8" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        )}
        
        <div className="flex items-center">
          <span className={`text-sm font-bold w-7 ${
            isCurrentQuestion ? 'text-orange-600' : 'text-gray-700'
          }`}>
            {question.displayNumber}.
          </span>
          
          {/* Answer Buttons - Compact spacing */}
          <div className="flex gap-1 flex-1 justify-center">
            {(question.answerType === 1 ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E']).map(choice => (
              <button
                key={choice}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isCurrentQuestion && onAnswerSelect) {
                    onAnswerSelect(choice as 'A' | 'B' | 'C' | 'D' | 'E', question.questionId);
                  }
                }}
                disabled={!isCurrentQuestion}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-sm font-semibold transition-all ${
                  answer === choice
                    ? isCurrentQuestion
                      ? 'bg-orange-500 text-white border-2 border-orange-600 shadow-md scale-110'
                      : 'bg-orange-500 text-white border-2 border-orange-600'
                    : isCurrentQuestion
                      ? 'bg-white text-gray-700 border-2 border-gray-300 active:bg-gray-50'
                      : 'bg-gray-50 text-gray-400 border-2 border-gray-200 cursor-not-allowed'
                }`}
              >
                {choice}
              </button>
            ))}
          </div>
          
          {/* Clear button - positioned on right border */}
          {isCurrentQuestion && answer && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onAnswerSelect) {
                  onAnswerSelect('' as any, question.questionId);
                }
              }}
              className="absolute -right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow-sm text-red-500 active:text-red-600 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M12.5 2.5a1 1 0 011.41 0l4 4a1 1 0 010 1.41l-8 8a1 1 0 01-.7.29H5a1 1 0 01-1-1v-4.2a1 1 0 01.29-.71l8-8a1 1 0 01.71-.29zm0 2.83L6 11.83V14h2.17l6.5-6.5L12.5 5.33z"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Modal Content - Responsive width for tablets */}
      <div 
        className="relative bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between px-3 pb-1 sm:px-4">
          <h3 className="text-xl font-bold text-gray-900">Optik Form</h3>
          <button
            onClick={onClose}
            className="p-1.5 -mr-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FaTimes size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content - Add padding-bottom for fixed footer */}
        <div className={`flex-1 overflow-y-auto px-3 -webkit-overflow-scrolling-touch sm:px-4 ${
          (isTestMode || (totalTime && totalTime > 0)) && !isTestCompleted ? 'pb-20' : 'pb-safe'
        }`}>
          {/* Group Selector */}
          {qbGroups.length > 0 && (
            <div className="mb-3">
              <div className="relative ios-select-wrapper">
                <select
                  value={selectedGroupIndex}
                  onChange={(e) => {
                    setIsUserSelection(true);
                    setSelectedGroupIndex(Number(e.target.value));
                  }}
                  className="w-full px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    // iOS-specific fixes
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: 'none',
                    backgroundPosition: 'right 0.7rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.2em 1.2em',
                    // Ensure text doesn't overlap with arrow
                    textIndent: '0.01px',
                    textOverflow: 'ellipsis',
                    // Fix iOS zoom on focus
                    fontSize: '16px'
                  }}
                >
                  {qbGroups.map((group, index) => (
                    <option key={index} value={index}>
                      {group.groupName || `Grup ${index + 1}`}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6l4 4 4-4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Questions List with Sections */}
          <div className="space-y-2 pb-3">
            {(() => {
                // Single group selected
                let questionsToDisplay: any[] = [];
                
                if (qbGroups.length === 0) {
                  questionsToDisplay = questions.map((question, index) => ({
                    ...question,
                    displayNumber: index + 1
                  }));
                } else if (selectedGroupIndex >= 0) {
                  const selectedGroup = qbGroups[selectedGroupIndex];
                  if (!selectedGroup) return null;
                  
                  let questionCounter = 1;
                  selectedGroup.courses?.forEach(course => {
                    course.questions?.forEach(question => {
                      if (!question.isCommonText) {
                        questionsToDisplay.push({
                          ...question,
                          displayNumber: questionCounter++,
                          globalIndex: questions.findIndex(q => q.questionId === question.questionId)
                        });
                      }
                    });
                  });
                }
                
                return questionsToDisplay.map((question) => renderQuestion(question));
            })()}
          </div>

          {/* Stats - Compact Design */}
          <div className="mt-3 mb-1 p-2 sm:p-3 bg-gray-100 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-600">İlerleme</p>
                <p className="text-base font-bold text-gray-800">
                  {(() => {
                    if (qbGroups.length === 0) {
                      // Count only real answers (exclude '0')
                      const answeredCount = Object.keys(opticalAnswers).filter(key => opticalAnswers[key] && opticalAnswers[key] !== '0').length;
                      return `${answeredCount} / ${questions.length}`;
                    } else {
                      const selectedGroup = qbGroups[selectedGroupIndex];
                      let totalCount = 0;
                      let answeredCount = 0;

                      selectedGroup?.courses?.forEach(course => {
                        course.questions?.forEach(question => {
                          if (!question.isCommonText && question.questionId) {
                            totalCount++;
                            const answer = opticalAnswers[question.questionId];
                            if (answer && answer !== '0') {
                              answeredCount++;
                            }
                          }
                        });
                      });

                      return `${answeredCount} / ${totalCount}`;
                    }
                  })()}
                </p>
              </div>
              
              {/* Progress Circle */}
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 64 64">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="#3b82f6"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(() => {
                      const total = qbGroups.length === 0
                        ? questions.length
                        : qbGroups[selectedGroupIndex]?.courses?.reduce((acc, course) =>
                            acc + (course.questions?.filter(q => !q.isCommonText).length || 0), 0) || 0;
                      const answered = qbGroups.length === 0
                        ? Object.keys(opticalAnswers).filter(key => opticalAnswers[key] && opticalAnswers[key] !== '0').length
                        : qbGroups[selectedGroupIndex]?.courses?.reduce((acc, course) =>
                            acc + (course.questions?.filter(q => !q.isCommonText && q.questionId && opticalAnswers[q.questionId] && opticalAnswers[q.questionId] !== '0').length || 0), 0) || 0;
                      return total > 0 ? (answered / total) * 176 : 0;
                    })()} 176`}
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Fixed Footer - Test Complete Button */}
        {(isTestMode || (totalTime && totalTime > 0)) && !isTestCompleted && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white shadow-lg rounded-b-3xl">
            <button
              onClick={() => setShowConfirmModal(true)}
              className="w-full py-2.5 px-3 bg-blue-500 text-white rounded-xl font-medium text-lg flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Sınavı Bitir</span>
            </button>
          </div>
        )}
      </div>
      
      {/* Test Complete Confirmation Modal */}
      <TestCompleteConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          onTestComplete?.();
          onClose();
        }}
        isMobile={true}
      />
    </div>
  );
};